import React from 'react';
import './flightVerticalOutLineSection.less';
import './flightVerticalOutLineSection.mobile.less';

import { FaPlane, FaExchangeAlt } from 'react-icons/fa';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import {
  CABIN_LABEL_KEYS,
  fromWireCabin,
} from '@skygroup/shared/utils/cabinClass';
import {
  formatMinutes,
  getDayOffset,
  getLayoverMinutes,
  getLegDurationMinutes,
  getSegmentFlyingMinutes,
} from '@skygroup/shared/utils/flightDuration';

interface Props {
  flightData: any;
}

/** "OS 858" — carrier code + number, without doubling a code the number already carries. */
const formatFlightNo = (airlineId: unknown, flightNumber: unknown): string => {
  const id = String(airlineId ?? '').trim();
  const num = String(flightNumber ?? '').trim();
  const bare = id && num.startsWith(id) ? num.slice(id.length) : num;
  return [id, bare].filter(Boolean).join(' ');
};

/** The airport's name as a hover hint, when the supplier sent one beyond the code. */
const placeTitle = (place?: { code?: string; name?: string } | null) =>
  place?.name && place.name !== place.code ? place.name : undefined;

/** Below this, a connection reads as "short"; at or above it, "long". */
const LONG_WAIT_HOURS = 3;

/**
 * The itinerary of one leg, opened from the leg row's "1 stop ▾" button:
 *
 *   [logo] OS 802 Economy   06:00 TLV ──── 3h ────── 09:00 VIE   Wed, 01 Jul
 *      ⏱ 2h 30m · Connect in airport VIE · Short wait
 *   [logo] OS 633 Economy   11:30 VIE ──── 4h 10m ── 16:40 TBS   Wed, 01 Jul
 *   Total: 16h · Arrives Thu, 02 Jul 2026
 *
 * One line per segment (two in a narrow container) — the old panel spent three
 * lines and a header on every hop.
 *
 * Every duration here is timezone-safe (shared/utils/flightDuration.ts): flying
 * time from the supplier, ground time as a same-airport subtraction, the total
 * from the leg helper. The old panel subtracted the departure airport's clock
 * from the arrival airport's — the "total travel time is wrong with stops" bug.
 */
const FlightVerticalOutLineSection: React.FC<Props> = ({ flightData }) => {
  const { searchDatePicker: t, general: t_general } = useSelector(
    (data: RootState) => data.config.translations
  );

  const segments: any[] = Array.isArray(flightData)
    ? flightData
    : flightData
    ? [flightData]
    : [];

  if (segments.length < 1) {
    return null;
  }

  const fmt = (minutes: number | null) =>
    formatMinutes(minutes, t_general?.h, t_general?.min);

  const last = segments[segments.length - 1];
  const arrivalMoment = moment(last?.arrival_date * 1000).utc();
  const totalLabel = fmt(getLegDurationMinutes(segments));

  return (
    <div className="flight-stop-details-wrapper fl-itin">
      <ol className="fl-itin-list">
        {segments.map((segment, i) => {
          const dep = moment(segment?.departure_date * 1000).utc();
          const arr = moment(segment?.arrival_date * 1000).utc();
          const dayOffset = getDayOffset(
            segment?.departure_date,
            segment?.arrival_date
          );
          const flyingLabel = fmt(getSegmentFlyingMinutes(segment));

          const airline = segment?.airline;
          const operatedAirline = segment?.operated_airline;
          const isOperatedByOther =
            operatedAirline?.name &&
            operatedAirline?.id &&
            operatedAirline.id !== airline?.id;

          const rawCabin = segment?.cabin;
          const cabinOption = fromWireCabin(
            typeof rawCabin === 'string' ? rawCabin : undefined
          );
          const cabinLabel = cabinOption
            ? t[CABIN_LABEL_KEYS[cabinOption]]
            : undefined;

          const next = segments[i + 1];
          const layoverMinutes = next ? getLayoverMinutes(segment, next) : null;
          const isLongWait =
            layoverMinutes !== null && layoverMinutes >= LONG_WAIT_HOURS * 60;
          const landingCode = segment?.to?.code;
          const boardingCode = next?.from?.code;
          const changesAirport =
            !!next &&
            !!landingCode &&
            !!boardingCode &&
            landingCode !== boardingCode;

          return (
            <li
              key={`${segment?.flight_number}-${segment?.departure_date}-${i}`}
            >
              {/* ONE line per segment: carrier · dep → (flying time) → arr · date */}
              <div className="fl-itin-seg">
                <div className="fl-itin-carrier" title={airline?.name}>
                  {airline?.image ? (
                    <img
                      src={airline.image}
                      className="logo"
                      alt={airline?.name ?? ''}
                    />
                  ) : (
                    <FaPlane className="fl-itin-carrier-icon" />
                  )}
                  {segment?.flight_number ? (
                    <span className="fl-itin-flight-no" title={t.flight_number}>
                      {formatFlightNo(airline?.id, segment.flight_number)}
                    </span>
                  ) : null}
                  {cabinLabel && (
                    <span className="flight-cabin-pill">{cabinLabel}</span>
                  )}
                </div>

                <div className="fl-itin-route">
                  <span className="fl-itin-point">
                    <span className="fl-itin-time">{dep.format('HH:mm')}</span>
                    <span
                      className="fl-itin-code"
                      title={placeTitle(segment?.from)}
                    >
                      {segment?.from?.code}
                    </span>
                  </span>
                  <span className="fl-itin-track">
                    <span className="fl-itin-track-line" />
                    {flyingLabel ? (
                      <span className="fl-itin-flying">{flyingLabel}</span>
                    ) : null}
                  </span>
                  <span className="fl-itin-point is-arr">
                    <span className="fl-itin-time">
                      {arr.format('HH:mm')}
                      {dayOffset !== 0 && (
                        <sup className="fl-itin-day-offset">
                          {dayOffset > 0 ? `+${dayOffset}` : dayOffset}
                        </sup>
                      )}
                    </span>
                    <span
                      className="fl-itin-code"
                      title={placeTitle(segment?.to)}
                    >
                      {segment?.to?.code}
                    </span>
                  </span>
                </div>

                <span className="fl-itin-date">
                  {dep.format('ddd, DD MMM')}
                </span>
              </div>

              {isOperatedByOther ? (
                <div className="fl-itin-operated">
                  {`${t.operated_by_airlines}: ${operatedAirline.name}`}
                </div>
              ) : null}

              {next && (
                <div
                  className={`airport-wait-duration fl-itin-layover${
                    isLongWait ? ' long-wait' : ''
                  }${changesAirport ? ' is-airport-change' : ''}`}
                >
                  <ClockCircleOutlined className="fl-itin-layover-icon" />
                  {layoverMinutes !== null && (
                    <span className="time">{fmt(layoverMinutes) || '—'}</span>
                  )}
                  <span className="fl-itin-layover-where">
                    {`${t.connect_in_airport ?? ''}`.trim()}{' '}
                    <b>
                      {changesAirport
                        ? `${landingCode} → ${boardingCode}`
                        : landingCode}
                    </b>
                  </span>
                  {layoverMinutes !== null && (
                    <span className="wait-type">
                      {isLongWait ? t.long_wait : t.short_wait}
                    </span>
                  )}
                  {changesAirport && t.airportChangeTitle ? (
                    <span className="fl-itin-airport-change">
                      <FaExchangeAlt /> {t.airportChangeTitle}
                    </span>
                  ) : null}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <div className="flight-general-info-wrapper fl-itin-summary">
        {totalLabel ? (
          <div>
            <span>{t.journey_duration}: </span>
            <strong>{totalLabel}</strong>
          </div>
        ) : null}
        <div>
          <span>{t.arrives}: </span>
          <strong>
            {arrivalMoment.format('ddd, DD MMM YYYY')} ·{' '}
            {arrivalMoment.format('HH:mm')} {last?.to?.code}
          </strong>
        </div>
      </div>
    </div>
  );
};

export default FlightVerticalOutLineSection;
